/**
 * Conversa direta com qualquer pessoa (docs/spec/friends.md, "Mensagens de quem não é amigo"): se
 * dá para abrir uma, o abrir que vale também na janela destacada, e o "Adicionar amigo" e o
 * "Bloquear" que a janela de conversa, as "Outras conversas" e o perfil usam.
 */
import type { User } from '../shared.js';
import { useApp } from '../store.js';
import { askConfirm, askText } from '../ui/prompt.js';
import { POPOUT } from '../windowMode.js';
import { postWindows } from '../room/windows.js';
import { useFriendsUi } from './friendsUi.js';

type T = (key: string, params?: Record<string, string | number>) => string;

/** Dá para conversar com quem não é amigo: o painel ligado e um servidor que aceita. */
export function useCanDm(): boolean {
  return useApp((s) => s.settings.showFriendsChat && s.serverFeatures.imsAnyone);
}

/**
 * Abre a conversa embaixo. A janela destacada não tem o painel: a conversa abre na principal.
 * `seed`: quem é a pessoa, para a janela ter o nome antes de o histórico chegar.
 */
export function openDm(userId: string, seed?: User): void {
  if (!POPOUT) return useApp.getState().openChat(userId, seed);
  postWindows({ t: 'open-dm', userId });
  window.opener?.focus();
}

/** "Adicionar amigo": o recado opcional e o pedido; enviado, o painel mostra os Pedidos. */
export async function askFriendRequest(user: Pick<User, 'id' | 'displayName'>, t: T): Promise<void> {
  const note = await askText({ title: t('friends.request.title', { name: user.displayName }), label: t('friends.request.note'), maxLength: 300, allowEmpty: true, action: t('friends.request.send') });
  if (note === null) return;
  if ((await useApp.getState().requestContact({ userId: user.id }, note || null)) && !POPOUT) useFriendsUi.getState().show('requests');
}

/** "Bloquear", depois de confirmar: a janela da conversa fecha. */
export async function askBlock(user: Pick<User, 'id' | 'displayName'>, t: T): Promise<void> {
  const sure = await askConfirm({ title: t('friends.menu.block'), message: t('friends.block.confirm', { name: user.displayName }), action: t('friends.menu.block'), danger: true });
  if (!sure) return;
  useFriendsUi.getState().closeWindow(user.id);
  await useApp.getState().contactAction(user.id, 'block');
}
